import { BundleItem, OrderItem, OrderItemComponent, Product, ProductVariant } from '../types';
import { effectiveVatRate, LEGACY_VAT_RATE, ProductCategory, round2 } from './vat';

/**
 * Produktpaket: en produkt som består av andra produkter.
 *
 * - Admin sätter paketets pris. Värdet är summan av delarnas egna priser.
 * - Priset fördelas på delarna efter deras egna priser, och varje del får
 *   sin produkts moms. Den sista delen tar öresresten, så att andelarna
 *   alltid blir exakt paketets pris (samma regel som order_line_snapshot).
 * - Vikt och inköp är summan av delarna.
 * - Ordern sparar delarna med sin andel (OrderItem.components). Kvitto,
 *   faktura, momsrapport och frakt räknar på dem.
 */

export const MAX_BUNDLE_PART_QTY = 20;

export const isBundle = (p?: Pick<Product, 'bundleItems' | 'variants'> | null): boolean =>
    !!p && ((p.bundleItems?.length ?? 0) > 0 || !!p.variants?.some(v => (v.bundleItems?.length ?? 0) > 0));

/** Innehållet för en vald variant: variantens eget, annars paketets. */
export const bundleItemsFor = (p: Pick<Product, 'bundleItems'>, variant?: Pick<ProductVariant, 'bundleItems'> | null): BundleItem[] =>
    (variant?.bundleItems?.length ? variant.bundleItems : p.bundleItems) || [];

/**
 * Kundens val i ett paket: vilken variant av en del (t.ex. Flaskan i Pastel
 * Pink). Nyckeln är delens produkt-id. Gäller delar som saknar bestämd
 * variant men vars produkt har varianter.
 */
export type BundleChoices = Record<string, string>;

/** Delarna där kunden väljer variant, i paketets ordning, en per produkt. */
export const choiceProducts = (items: BundleItem[], catalog: Product[]): Product[] => {
    const out: Product[] = [];
    items.forEach(item => {
        if (item.variantId || out.some(p => p.id === item.productId)) return;
        const p = catalog.find(x => x.id === item.productId);
        if (p && (p.variants?.length ?? 0) > 0) out.push(p);
    });
    return out;
};

/** Innehållet med kundens val ifyllda. Delar utan val lämnas som de är. */
export const applyChoices = (items: BundleItem[], choices?: BundleChoices | null): BundleItem[] =>
    !choices ? items : items.map(item => (!item.variantId && choices[item.productId] ? { ...item, variantId: choices[item.productId] } : item));

/** Har kunden valt för alla delar som kräver det (och finns valen)? */
export const choicesComplete = (items: BundleItem[], catalog: Product[], choices?: BundleChoices | null): boolean =>
    choiceProducts(items, catalog).every(p => !!choices?.[p.id] && !!p.variants?.some(v => v.id === choices[p.id]));

/** Samma val ger samma rad i varukorgen. */
export const choicesKey = (choices?: BundleChoices | null): string =>
    choices ? Object.keys(choices).sort().map(k => `${k}:${choices[k]}`).join(',') : '';

/** Syns produkten i butiken för gruppen? Utan grupp (allmänna butiken) syns bara produkter för alla. */
export const visibleForClass = (p: Pick<Product, 'visibleClassIds'>, classId?: string | null): boolean => {
    const ids = p.visibleClassIds || [];
    return ids.length === 0 || (!!classId && ids.includes(classId));
};

/** En del med det som behövs för pris, moms, vikt och inköp. */
export interface BundlePart {
    productId: string;
    variantId?: string;
    name: string;
    variantName?: string;
    quantity: number;
    unitPrice: number;
    /** null = delen saknar kategori och kan inte säljas. */
    vatRate: number | null;
    purchasePrice: number;
    weightGrams?: number;
    imageUrl?: string;
    /** Produkten eller varianten finns inte längre. */
    missing?: boolean;
    /** Delen är själv ett paket (inte tillåtet). */
    nested?: boolean;
    /** Produkten har varianter (t.ex. färger) och kunden väljer vilken i butiken. */
    customerChoice?: boolean;
}

export const resolveBundle = (
    items: BundleItem[],
    products: Product[],
    categories: ProductCategory[],
    at: Date = new Date()
): BundlePart[] => {
    const byId = new Map(products.map(p => [p.id, p]));
    const catById = new Map(categories.map(c => [c.id, c]));
    return items.map(item => {
        const p = byId.get(item.productId);
        if (!p) return { productId: item.productId, variantId: item.variantId, name: 'Produkt som tagits bort', quantity: item.quantity, unitPrice: 0, vatRate: null, purchasePrice: 0, missing: true };
        const v = item.variantId ? p.variants?.find(x => x.id === item.variantId) : undefined;
        const cat = p.categoryId ? catById.get(p.categoryId) : undefined;
        return {
            productId: p.id,
            variantId: item.variantId,
            name: p.name,
            variantName: v?.name,
            quantity: item.quantity,
            unitPrice: v ? v.price : p.price,
            vatRate: cat ? effectiveVatRate(cat, at) : null,
            purchasePrice: (v?.purchasePrice ?? p.purchasePrice) || 0,
            weightGrams: v?.weightGrams ?? p.weightGrams,
            imageUrl: v?.imageUrl || p.images?.[0] || p.imageUrl,
            missing: !!item.variantId && !v,
            nested: isBundle(p),
            customerChoice: !item.variantId && (p.variants?.length ?? 0) > 0,
        };
    });
};

/**
 * Bilden för en vara i varukorgen och liknande små rutor: variantens bild,
 * annars produktens, och för ett paket utan egen bild den första delen som
 * har en bild. undefined när ingen bild finns (visa då en ikon).
 */
export const productImage = (product: Product, variant: ProductVariant | undefined, catalog: Product[], choices?: BundleChoices | null): string | undefined => {
    const own = variant?.images?.[0] || variant?.imageUrl || product.images?.[0] || product.imageUrl;
    if (own) return own;
    if (!isBundle(product)) return undefined;
    const parts = resolveBundle(applyChoices(bundleItemsFor(product, variant), choices), catalog, []);
    return parts.find(p => p.imageUrl)?.imageUrl;
};

/**
 * Paketets uppdelning för kunden och revisorn: varje del med sitt ordinarie
 * pris, sin andel av paketpriset (rabatten dragen på delarna med högst moms,
 * inom en sats efter ordinarie pris) och sin moms, och hur stor paketrabatten är.
 */
export interface BreakdownPart {
    name: string;
    variantName?: string | null;
    quantity: number;
    unitPrice: number;
    /** Delens andel av ett pakets pris (alla dess exemplar), inklusive moms. */
    allocated: number;
    vatRate?: number | null;
}
export interface BreakdownRow { label: string; ordinary: number; allocated: number; vatRate: number | null }
export interface BundleBreakdown { rows: BreakdownRow[]; ordinary: number; discount: number }

export const bundleBreakdown = (parts: BreakdownPart[], price: number): BundleBreakdown => {
    const rows = parts.map(p => ({
        label: `${p.quantity > 1 ? `${p.quantity} × ` : ''}${p.name}${p.variantName ? ` (${String(p.variantName).toLocaleLowerCase('sv-SE')})` : ''}`,
        ordinary: round2(p.unitPrice * p.quantity),
        allocated: round2(p.allocated),
        vatRate: p.vatRate ?? null,
    }));
    const ordinary = round2(rows.reduce((s, r) => s + r.ordinary, 0));
    return { rows, ordinary, discount: Math.max(0, round2(ordinary - price)) };
};

/** "204,35 kr", heltal utan decimaler: "239 kr". */
export const breakdownKr = (n: number): string =>
    `${n.toLocaleString('sv-SE', { minimumFractionDigits: Number.isInteger(round2(n)) ? 0 : 2, maximumFractionDigits: 2 })} kr`;

/** "Flaskan · ord. 239 kr → 204,35 kr · 25 % moms" (kvitto, faktura). */
export const breakdownRowText = (r: BreakdownRow): string =>
    `${r.label} · ord. ${breakdownKr(r.ordinary)} → ${breakdownKr(r.allocated)}${r.vatRate !== null ? ` · ${r.vatRate} % moms` : ''}`;

/**
 * Är rabatten dragen på produkten med högst moms? Avgörs av siffrorna på
 * ordern (äldre ordrar har rabatten fördelad efter ordinarie pris): paketet
 * har flera satser och delarna med lägre sats står kvar på ordinarie pris.
 */
export const discountOnHighestRate = (rows: { vatRate?: number | null; ordinary: number; allocated: number }[]): boolean => {
    const rates = rows.map(r => r.vatRate).filter((r): r is number => typeof r === 'number');
    if (new Set(rates).size < 2) return false;
    const top = Math.max(...rates);
    return rows.every(r => r.vatRate === top || Math.abs(r.allocated - r.ordinary) < 0.005);
};

/** Hur rabatten är dragen, för texten under paketet. */
export const discountRuleText = (onHighestRate: boolean): string =>
    onHighestRate ? 'dragen på produkten med högst moms' : 'fördelad på produkterna efter ordinarie pris';

/** "Paketrabatt −49 kr, dragen på produkten med högst moms". */
export const breakdownDiscountText = (b: BundleBreakdown): string =>
    `Paketrabatt −${breakdownKr(b.discount)}, ${discountRuleText(discountOnHighestRate(b.rows))}`;

export const bundleValue = (parts: Pick<BundlePart, 'unitPrice' | 'quantity'>[]): number =>
    round2(parts.reduce((s, p) => s + p.unitPrice * p.quantity, 0));

/** Paketpriset fördelat på delarna efter deras egna priser. Den sista delen tar öresresten. */
/**
 * Paketpriset per del (databasen räknar likadant: bundle_components).
 *
 * Rabatten (ordinarie pris för delarna minus paketpriset) dras på delarna med
 * högst momssats. Räcker de inte till tas resten från nästa sats. Inom en sats
 * fördelas rabatten efter delarnas ordinarie pris, och den sista delen tar
 * öresresten. Paket med en sats blir därför som förut: rabatten fördelad
 * efter ordinarie pris. Kostar paketet mer än delarna (ingen rabatt) fördelas
 * priset efter delarnas priser.
 */
export const allocate = (price: number, parts: (Pick<BundlePart, 'unitPrice' | 'quantity'> & { vatRate?: number | null })[]): number[] => {
    if (parts.length === 0) return [];
    const full = parts.map(p => round2(p.unitPrice * p.quantity));
    const value = round2(full.reduce((s, v) => s + v, 0));
    let remaining = round2(value - price);

    if (remaining <= 0) {
        const out: number[] = [];
        let used = 0;
        parts.forEach((p, i) => {
            if (i === parts.length - 1) { out.push(round2(price - used)); return; }
            const share = value > 0 ? round2((price * full[i]) / value) : 0;
            used = round2(used + share);
            out.push(share);
        });
        return out;
    }

    const rateOf = (i: number) => parts[i].vatRate ?? -1;
    const out = [...full];
    const rates = [...new Set(parts.map((_, i) => rateOf(i)))].sort((a, b) => b - a);
    for (const rate of rates) {
        if (remaining <= 0) break;
        const idx = parts.map((_, i) => i).filter(i => rateOf(i) === rate);
        const groupValue = round2(idx.reduce((s, i) => s + full[i], 0));
        if (groupValue <= 0) continue;
        const take = Math.min(remaining, groupValue);
        let taken = 0;
        idx.forEach((i, k) => {
            const cut = k === idx.length - 1 ? round2(take - taken) : round2((take * full[i]) / groupValue);
            taken = round2(taken + cut);
            out[i] = round2(out[i] - cut);
        });
        remaining = round2(remaining - take);
    }
    return out;
};

export interface BundleVatRow { rate: number; gross: number; vat: number; }

/** Momsen i ett paket per sats, inklusive moms. */
export const bundleVat = (price: number, parts: BundlePart[]): BundleVatRow[] => {
    const alloc = allocate(price, parts);
    const byRate = new Map<number, number>();
    parts.forEach((p, i) => {
        const rate = p.vatRate ?? LEGACY_VAT_RATE;
        byRate.set(rate, round2((byRate.get(rate) || 0) + alloc[i]));
    });
    return [...byRate.entries()]
        .sort((a, b) => b[0] - a[0])
        .map(([rate, gross]) => ({ rate, gross, vat: round2((gross * rate) / (100 + rate)) }));
};

export interface BundleSummary {
    value: number;
    savings: number;
    vat: BundleVatRow[];
    purchasePrice: number;
    /** undefined om någon del saknar vikt. */
    weightGrams?: number;
    /** Det som hindrar att paketet säljs, i klartext. */
    problems: string[];
}

export const summarizeBundle = (price: number, parts: BundlePart[]): BundleSummary => {
    const value = bundleValue(parts);
    const problems: string[] = [];
    if (parts.length === 0) problems.push('Lägg till minst en produkt i paketet.');
    parts.forEach(p => {
        if (p.missing) problems.push(`${p.name}${p.variantId ? ' (varianten)' : ''} finns inte längre.`);
        else if (p.nested) problems.push(`${p.name} är själv ett paket. Ett paket kan inte innehålla paket.`);
        else if (p.vatRate === null) problems.push(`${p.name} saknar kategori, så momsen är okänd.`);
    });
    const allWeighed = parts.length > 0 && parts.every(p => typeof p.weightGrams === 'number');
    return {
        value,
        savings: round2(value - price),
        vat: bundleVat(price, parts),
        purchasePrice: round2(parts.reduce((s, p) => s + p.purchasePrice * p.quantity, 0)),
        weightGrams: allWeighed ? parts.reduce((s, p) => s + (p.weightGrams as number) * p.quantity, 0) : undefined,
        problems,
    };
};

/**
 * CoffeeNovas del exkl. moms för ett paket, med samma modell som
 * marginPerItem: hos säljaren minskar provisionen priset per del och momsen
 * per del; i kassan (online) är momsen på hela priset och provisionen en
 * kostnad utan moms ovanpå.
 */
export const bundleMargin = (price: number, parts: BundlePart[], commissionRate: number, online = false): number => {
    const alloc = allocate(price, parts);
    const share = online ? 1 : 1 - commissionRate;
    const exVat = parts.reduce((s, p, i) => s + (alloc[i] * share) / (1 + (p.vatRate ?? LEGACY_VAT_RATE) / 100), 0);
    const purchase = parts.reduce((s, p) => s + p.purchasePrice * p.quantity, 0);
    return round2(exVat - purchase - (online ? price * commissionRate : 0));
};

type RateOf = (x: { productId: string; vatRate?: number | null }) => number;
const snapshotRate: RateOf = x => x.vatRate ?? LEGACY_VAT_RATE;

export interface VatPart { rate: number; amount: number; share: number; }

/**
 * En orderrad delad på momssatser. Vanlig rad: en del. Paketrad: en del per
 * produkt i paketet med dess andel av priset. share = andel av radens belopp.
 */
export const vatParts = (item: OrderItem, rateOf: RateOf = snapshotRate): VatPart[] => {
    const comps = item.components || [];
    if (comps.length === 0) return [{ rate: rateOf(item), amount: item.priceAtMoment * item.quantity, share: 1 }];
    return comps.map(c => ({
        rate: rateOf(c),
        amount: c.allocated * item.quantity,
        share: item.priceAtMoment > 0 ? c.allocated / item.priceAtMoment : 1 / comps.length,
    }));
};

/** Det som ska plockas för en orderrad: paket blir sina produkter. */
export interface PickUnit {
    productId: string;
    variantId?: string;
    name: string;
    variantName?: string;
    quantity: number;
    /** Paketets namn när enheten ingår i ett paket. */
    bundleName?: string;
}

export const pickUnits = (item: OrderItem, nameOf: (productId: string, snapshot?: string) => string = (_, s) => s || 'Produkt'): PickUnit[] => {
    const comps: OrderItemComponent[] = item.components || [];
    if (comps.length === 0) {
        return [{ productId: item.productId, variantId: item.variantId, name: nameOf(item.productId, item.productNameSnapshot), variantName: item.variantName, quantity: item.quantity }];
    }
    const bundleName = nameOf(item.productId, item.productNameSnapshot) + (item.variantName ? ` (${item.variantName})` : '');
    return comps.map(c => ({
        productId: c.productId,
        variantId: c.variantId,
        name: nameOf(c.productId, c.name),
        variantName: c.variantName,
        quantity: c.quantity * item.quantity,
        bundleName,
    }));
};

/** "2 × Johannas favorit, Signature Blend" */
export const bundleContentsText = (parts: { name: string; variantName?: string; quantity: number }[]): string =>
    parts.map(p => `${p.quantity > 1 ? `${p.quantity} × ` : ''}${p.name}${p.variantName ? ` (${p.variantName.toLocaleLowerCase('sv-SE')})` : ''}`).join(', ');

/** Vad en paketrad innehåller, för kvitton och listor. null för vanliga rader. */
export const orderItemContents = (item: Pick<OrderItem, 'components'>): string | null =>
    item.components && item.components.length > 0 ? bundleContentsText(item.components) : null;
